/**
 * Request Board API — wraps the `/request_board` router on the eastview-os
 * backend (app/routers/request_board.py; contract in eastview-os
 * frontend_documentation/request-board-api.md, shipped 2026-10-14).
 *
 * Every route is open to any logged-in user (who can SEE the board is the
 * frontend's super-admin gate). Every mutation returns the full updated card
 * (attachment `url`s freshly signed, one-hour SAS) or `{ deleted, id }`; the
 * backend writes the history event(s), runs alert matching / delivery and
 * pushes `request_board_updated` to everyone else on the board.
 *
 * Ids: cards come back keyed by `_id` (string); comments / tasks / images /
 * attachments by `id`. Timestamps are naive UTC (read them with formatDateTime
 * / timestampMs, never `new Date(x)` directly).
 *
 * Errors are FastAPI `{ detail: "<code>" }` — see `errorMessage` below.
 */
import { losHttp } from "../lib/losHttp";

const cfg = (logName, extra = {}) => ({ logName, ...extra });
const data = (r) => r.data;
const enc = encodeURIComponent;

/** Friendly text for the backend's `detail` codes (anything else falls through). */
const ERROR_TEXT = {
  item_not_found: "That card no longer exists.",
  task_not_found: "That task no longer exists.",
  image_not_found: "That image was already removed.",
  attachment_not_found: "An attachment didn't finish uploading. Try again.",
  already_in_backend: "The card is already in Requires Backend Changes.",
  no_backend_details: "This card has no backend details to change.",
  file_too_large: "That file is too large (25 MB max).",
  title_required: "The request can't be empty.",
  backend_details_required: "Moving into Requires Backend Changes needs the backend details.",
  comment_required: "Sending back needs a comment.",
  empty_comment: "Write a comment or attach an image.",
  type_required: "Pick what's needed.",
  summary_required: "Add a summary.",
  summary_too_long: "The summary is limited to 140 characters.",
  invalid_column: "That column doesn't exist.",
  invalid_type: "That type isn't valid.",
  invalid_platform: "That company isn't valid.",
  invalid_need: "That tag isn't valid.",
  invalid_urgency: "That urgency isn't valid.",
  answer_required: "Write the answer first.",
  already_in_review: "The card is already in review.",
  invalid_alert_preferences: "Those alert settings couldn't be saved.",
  email_failed: "The test email couldn't be sent.",
  no_attachments: "Pick at least one image.",
  hit_too_soon: "Already counted. Give it a few seconds before counting it again.",
  item_closed: "This request is already approved, so it isn't counted any more.",
  hit_not_found: "That count was already removed.",
  not_your_hit: "You can only undo your own count.",
  no_items: "There are no cards to move.",
  too_many_items: "That's too many cards to move at once (100 max).",
};

// A route the backend doesn't serve yet comes back as FastAPI's generic 404/405.
const NOT_LIVE = new Set(["Not Found", "Method Not Allowed"]);

export const requestBoardErrorMessage = (err, fallback = "Something went wrong.") => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && NOT_LIVE.has(detail)) {
    return "This isn't switched on yet. It will work once the update is live.";
  }
  if (typeof detail === "string") return ERROR_TEXT[detail] || detail;
  return fallback;
};

export const requestBoardApi = {
  // GET /meta → { me, people[], columns, types, platforms, blockerTypes, summaryMax }
  getMeta: () => losHttp.get("/request_board/meta", cfg("request_board.meta")).then(data),

  // GET /items?doneLimit → { items[], counts{…, total} }
  getItems: ({ doneLimit = 50 } = {}) =>
    losHttp
      .get("/request_board/items", cfg("request_board.items", { params: { doneLimit } }))
      .then(data),

  // GET /items/{idOrNumber} → item (deep link `?item=`)
  getItem: (idOrNumber) =>
    losHttp.get(`/request_board/items/${enc(idOrNumber)}`, cfg("request_board.item")).then(data),

  // POST /items { needs[], urgency, position, title, description, platform, context, area, attachmentIds }
  // (always filed into `requests`; position is within the card's section, Urgent or Queue)
  createItem: (body) => losHttp.post("/request_board/items", body, cfg("request_board.create")).then(data),

  // PATCH /items/{id} { title?, description?, needs?, urgency?, answer?, platform?, context?, area? }
  updateItem: (id, patch) =>
    losHttp.patch(`/request_board/items/${enc(id)}`, patch, cfg("request_board.update")).then(data),

  // DELETE /items/{id} → { deleted, id }
  deleteItem: (id) => losHttp.delete(`/request_board/items/${enc(id)}`, cfg("request_board.delete")).then(data),

  // POST /items/{id}/move { to, beforeId, urgency?, needs?, comment? { body, attachmentIds, mentions } }
  // `urgency` places a card in the Urgent section or the Queue of `requests`.
  moveItem: (id, body) =>
    losHttp.post(`/request_board/items/${enc(id)}/move`, body, cfg("request_board.move")).then(data),

  // POST /items/move_many { itemIds[], to, from? } → { moved: [Item], skipped: [{ id, reason }] }
  // Complete All / Approve All (update request 2026-10-20).
  moveMany: (body) =>
    losHttp.post("/request_board/items/move_many", body, cfg("request_board.move_many")).then(data),

  // POST /items/{id}/answer { answer } — answers a question and moves the card to
  // Completed - Awaiting Review in one event (`answered`). Update request 2026-10-14.
  answerItem: (id, answer) =>
    losHttp.post(`/request_board/items/${enc(id)}/answer`, { answer }, cfg("request_board.answer")).then(data),

  // POST /items/{id}/backend_details — moves INTO needs_backend with the details
  addBackendDetails: (id, body) =>
    losHttp
      .post(`/request_board/items/${enc(id)}/backend_details`, body, cfg("request_board.backend_details"))
      .then(data),

  // PUT /items/{id}/backend_details — edit (no move)
  editBackendDetails: (id, body) =>
    losHttp
      .put(`/request_board/items/${enc(id)}/backend_details`, body, cfg("request_board.backend_details.edit"))
      .then(data),

  // PATCH /items/{id}/backend_details/tasks/{taskId} { done }
  setTask: (id, taskId, done) =>
    losHttp
      .patch(
        `/request_board/items/${enc(id)}/backend_details/tasks/${enc(taskId)}`,
        { done },
        cfg("request_board.task"),
      )
      .then(data),

  // POST /items/{id}/comments { body, attachmentIds, mentions }
  addComment: (id, body) =>
    losHttp.post(`/request_board/items/${enc(id)}/comments`, body, cfg("request_board.comment")).then(data),

  // POST /items/{id}/images { attachmentIds }
  addImages: (id, attachmentIds) =>
    losHttp
      .post(`/request_board/items/${enc(id)}/images`, { attachmentIds }, cfg("request_board.images.add"))
      .then(data),

  // DELETE /items/{id}/images/{imageId}
  removeImage: (id, imageId) =>
    losHttp
      .delete(`/request_board/items/${enc(id)}/images/${enc(imageId)}`, cfg("request_board.images.remove"))
      .then(data),

  // POST /attachments (multipart `uploadFiles`) → [{ id, name, type, size, url }]
  upload: (files) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("uploadFiles", f, f.name || "pasted-screenshot.png"));
    return losHttp.post("/request_board/attachments", fd, cfg("request_board.upload")).then(data);
  },

  // GET /activity?itemId&kinds&q&before&limit → { events[], next }
  getActivity: ({ itemId, kinds, q, before, limit = 100 } = {}) =>
    losHttp
      .get(
        "/request_board/activity",
        cfg("request_board.activity", {
          params: Object.fromEntries(
            Object.entries({ itemId, kinds, q, before, limit }).filter(
              ([, v]) => v !== undefined && v !== null && v !== "",
            ),
          ),
        }),
      )
      .then(data),

  // POST /items/{id}/hits → { item, hit: { id, by, at } } — "I ran into this
  // again": one count per click, repeat clicks allowed (update request 2026-10-20).
  addHit: (id) =>
    losHttp.post(`/request_board/items/${enc(id)}/hits`, {}, cfg("request_board.hits.add")).then(data),

  // DELETE /items/{id}/hits/{hitId} → item — undo your own count
  removeHit: (id, hitId) =>
    losHttp
      .delete(`/request_board/items/${enc(id)}/hits/${enc(hitId)}`, cfg("request_board.hits.remove"))
      .then(data),

  // GET / PUT /alert_preferences — the caller's { scope, rules, delivery }
  getAlertPrefs: () => losHttp.get("/request_board/alert_preferences", cfg("request_board.prefs")).then(data),
  saveAlertPrefs: (prefs) =>
    losHttp.put("/request_board/alert_preferences", prefs, cfg("request_board.prefs.save")).then(data),

  // POST /alert_preferences/test_email → { sent, to }
  sendTestEmail: () =>
    losHttp.post("/request_board/alert_preferences/test_email", {}, cfg("request_board.test_email")).then(data),
};

export default requestBoardApi;
